import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  ExternalLink, 
  Copy, 
  Edit2, 
  Save,
  Trash2,
  Plus,
  BookOpen,
  Globe,
  FileText,
  BookMarked,
  Quote,
  MessageSquare,
  Lightbulb,
  Link as LinkIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';

const sourceTypeIcons = {
  WEBSITE: Globe,
  BOOK: BookMarked,
  JOURNAL: FileText,
  PDF: FileText,
  OTHER: BookOpen,
};

const noteTypeLabels = {
  GENERAL: 'General',
  SUMMARY: 'Summary',
  QUOTE: 'Quote',
  EVALUATION: 'Evaluation',
  RELEVANCE: 'Relevance',
};

const noteTypeIcons = {
  GENERAL: MessageSquare,
  SUMMARY: FileText,
  QUOTE: Quote,
  EVALUATION: Lightbulb,
  RELEVANCE: LinkIcon,
};

const SourceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [citations, setCitations] = useState({});
  const [newNote, setNewNote] = useState({ noteType: 'GENERAL', content: '', pageRef: '' });
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteData, setEditingNoteData] = useState({});

  useEffect(() => {
    fetchSource();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchSource = async () => {
    try {
      const res = await api.get(`/sources/${id}`);
      setSource(res.data.source);
      setEditData({
        ...res.data.source,
        authors: res.data.source.authors?.join(', ') || ''
      });
      
      // Fetch citations
      const [mla, apa, chicago] = await Promise.all([
        api.get(`/sources/${id}/citation/mla`),
        api.get(`/sources/${id}/citation/apa`),
        api.get(`/sources/${id}/citation/chicago`)
      ]);
      setCitations({
        mla: mla.data,
        apa: apa.data,
        chicago: chicago.data
      });
    } catch (err) {
      toast.error('Failed to load source');
      navigate('/research');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const authors = editData.authors
        ? editData.authors.split(',').map(a => a.trim()).filter(Boolean)
        : [];

      await api.patch(`/sources/${id}`, {
        ...editData,
        authors
      });

      setSource({ ...source, ...editData, authors });
      setEditing(false);
      toast.success('Source updated');
      
      // Refresh citations
      const [mla, apa, chicago] = await Promise.all([
        api.get(`/sources/${id}/citation/mla`),
        api.get(`/sources/${id}/citation/apa`),
        api.get(`/sources/${id}/citation/chicago`)
      ]);
      setCitations({ mla: mla.data, apa: apa.data, chicago: chicago.data });
    } catch (err) {
      toast.error('Failed to update source');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this source?')) return;

    try {
      await api.delete(`/sources/${id}`);
      toast.success('Source deleted');
      navigate('/research');
    } catch (err) {
      toast.error('Failed to delete source');
    }
  };

  const handleCopy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied!`);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.content.trim()) {
      toast.error('Please enter note content');
      return;
    }

    try {
      const res = await api.post('/notes', {
        sourceId: id,
        noteType: newNote.noteType,
        content: newNote.content,
        ...(newNote.pageRef && { pageRef: newNote.pageRef })
      });
      setSource({
        ...source,
        notes: [res.data.note, ...(source.notes || [])]
      });
      setNewNote({ noteType: 'GENERAL', content: '', pageRef: '' });
      toast.success('Note added');
    } catch (err) {
      toast.error('Failed to add note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await api.delete(`/notes/${noteId}`);
      setSource({
        ...source,
        notes: source.notes.filter(n => n.id !== noteId)
      });
      toast.success('Note deleted');
    } catch (err) {
      toast.error('Failed to delete note');
    }
  };

  const handleStartEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditingNoteData({ content: note.content, noteType: note.noteType, pageRef: note.pageRef || '' });
  };

  const handleSaveNote = async (noteId) => {
    try {
      const res = await api.patch(`/notes/${noteId}`, {
        content: editingNoteData.content,
        noteType: editingNoteData.noteType,
        pageRef: editingNoteData.pageRef || null
      });
      setSource(prev => ({
        ...prev,
        notes: prev.notes.map(n => n.id === noteId ? res.data.note : n)
      }));
      setEditingNoteId(null);
      toast.success('Note saved');
    } catch (err) {
      toast.error('Failed to save note');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Loading source...</div>
      </div>
    );
  }

  if (!source) return null;

  const Icon = sourceTypeIcons[source.sourceType] || BookOpen;

  return (
    <div className="max-w-4xl mx-auto animate-fade-in" data-testid="source-detail-page">
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="ghost"
          className="mb-4"
          onClick={() => navigate('/research')}
          data-testid="back-to-research-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Research
        </Button>

        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4 flex-1">
            <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
              <Icon className="w-6 h-6 text-slate-500" />
            </div>
            <div className="flex-1 min-w-0">
              <Badge variant="outline" className="mb-2 bg-slate-50">
                {source.sourceType}
              </Badge>
              {editing ? (
                <Input
                  value={editData.title || ''}
                  onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                  className="text-xl font-bold mb-2"
                  data-testid="edit-source-title"
                />
              ) : (
                <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                  {source.title}
                </h1>
              )}
              <p className="text-slate-600">
                {source.authors?.join(', ') || 'Unknown author'}
                {source.publishedDate && ` • ${source.publishedDate}`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {editing ? (
              <>
                <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                <Button onClick={handleSave} data-testid="save-source-btn">
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setEditing(true)} data-testid="edit-source-btn">
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button variant="outline" className="text-red-600 hover:bg-red-50" onClick={handleDelete}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="md:col-span-2 space-y-6">
          {/* Metadata */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Source Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {editing ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Authors</Label>
                      <Input
                        value={editData.authors}
                        onChange={(e) => setEditData({ ...editData, authors: e.target.value })}
                        placeholder="John Smith, Jane Doe"
                        data-testid="edit-source-authors"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Published Date</Label>
                      <Input
                        value={editData.publishedDate || ''}
                        onChange={(e) => setEditData({ ...editData, publishedDate: e.target.value })}
                        placeholder="2024"
                        data-testid="edit-source-date"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Publisher</Label>
                      <Input
                        value={editData.publisher || ''}
                        onChange={(e) => setEditData({ ...editData, publisher: e.target.value })}
                        data-testid="edit-source-publisher"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Source Type</Label>
                      <Select
                        value={editData.sourceType}
                        onValueChange={(v) => setEditData({ ...editData, sourceType: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="WEBSITE">Website</SelectItem>
                          <SelectItem value="BOOK">Book</SelectItem>
                          <SelectItem value="JOURNAL">Journal</SelectItem>
                          <SelectItem value="PDF">PDF</SelectItem>
                          <SelectItem value="OTHER">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>URL</Label>
                    <Input
                      value={editData.canonicalUrl || ''}
                      onChange={(e) => setEditData({ ...editData, canonicalUrl: e.target.value })}
                      type="url"
                      data-testid="edit-source-url"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Abstract / Description</Label>
                    <Textarea
                      value={editData.abstract || ''}
                      onChange={(e) => setEditData({ ...editData, abstract: e.target.value })}
                      placeholder="Brief abstract or description of this source..."
                      rows={3}
                      data-testid="edit-source-abstract"
                    />
                  </div>
                </>
              ) : (
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Authors</p>
                    <p className="font-medium">{source.authors?.join(', ') || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Published</p>
                    <p className="font-medium">{source.publishedDate || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Publisher</p>
                    <p className="font-medium">{source.publisher || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Accessed</p>
                    <p className="font-medium">{format(new Date(source.accessDate), 'MMM d, yyyy')}</p>
                  </div>
                  {source.canonicalUrl && (
                    <div className="col-span-2">
                      <p className="text-slate-500">URL</p>
                      <a
                        href={source.canonicalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                      >
                        {source.canonicalUrl.slice(0, 50)}...
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  )}
                  {source.abstract && (
                    <div className="col-span-2">
                      <p className="text-slate-500">Abstract</p>
                      <p className="text-slate-700 leading-relaxed mt-0.5">{source.abstract}</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Notes & Annotations</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddNote} className="mb-6 p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-2 mb-3">
                  <Select
                    value={newNote.noteType}
                    onValueChange={(v) => setNewNote({ ...newNote, noteType: v })}
                  >
                    <SelectTrigger className="w-40" data-testid="note-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(noteTypeLabels).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {newNote.noteType === 'QUOTE' && (
                    <Input
                      value={newNote.pageRef}
                      onChange={(e) => setNewNote({ ...newNote, pageRef: e.target.value })}
                      placeholder="Page ref (e.g. 42)"
                      className="w-36"
                      data-testid="note-pageref-input"
                    />
                  )}
                </div>
                <Textarea
                  value={newNote.content}
                  onChange={(e) => setNewNote({ ...newNote, content: e.target.value })}
                  placeholder="Add a note, quote, or evaluation..."
                  rows={3}
                  className="mb-3"
                  data-testid="note-content-input"
                />
                <Button type="submit" size="sm" data-testid="add-note-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Add Note
                </Button>
              </form>

              {source.notes?.length > 0 ? (
                <div className="space-y-3">
                  {source.notes.map((note) => {
                    const NoteIcon = noteTypeIcons[note.noteType] || MessageSquare;
                    const isEditingThis = editingNoteId === note.id;
                    return (
                      <div key={note.id} className="p-4 bg-white border border-slate-100 rounded-xl">
                        <div className="flex items-center justify-between mb-2">
                          <Badge variant="outline" className="gap-1">
                            <NoteIcon className="w-3 h-3" />
                            {noteTypeLabels[note.noteType]}
                          </Badge>
                          <div className="flex items-center gap-1">
                            {isEditingThis ? (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 text-indigo-600"
                                  onClick={() => handleSaveNote(note.id)}
                                >
                                  <Save className="w-3 h-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 text-slate-400"
                                  onClick={() => setEditingNoteId(null)}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 text-slate-400 hover:text-indigo-600"
                                  onClick={() => handleStartEditNote(note)}
                                  data-testid={`edit-note-${note.id}`}
                                >
                                  <Edit2 className="w-3 h-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 text-slate-400 hover:text-red-600"
                                  onClick={() => handleDeleteNote(note.id)}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>

                        {isEditingThis ? (
                          <div className="space-y-2">
                            <Textarea
                              value={editingNoteData.content}
                              onChange={e => setEditingNoteData(prev => ({ ...prev, content: e.target.value }))}
                              rows={3}
                              autoFocus
                            />
                            {editingNoteData.noteType === 'QUOTE' && (
                              <Input
                                value={editingNoteData.pageRef}
                                onChange={e => setEditingNoteData(prev => ({ ...prev, pageRef: e.target.value }))}
                                placeholder="Page reference (e.g. 42)"
                              />
                            )}
                          </div>
                        ) : (
                          <>
                            <p className={`text-slate-700 whitespace-pre-wrap ${note.noteType === 'QUOTE' ? 'italic font-mono text-sm' : ''}`}>
                              {note.noteType === 'QUOTE' ? `"${note.content}"` : note.content}
                            </p>
                            {note.noteType === 'QUOTE' && note.pageRef && (
                              <p className="text-xs text-slate-500 mt-1">— p. {note.pageRef}</p>
                            )}
                          </>
                        )}

                        <p className="text-xs text-slate-400 mt-2">
                          {format(new Date(note.createdAt), 'MMM d, yyyy')}
                        </p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-slate-500 py-4">No notes yet</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - Citations */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Citations</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="mla">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="mla">MLA</TabsTrigger>
                  <TabsTrigger value="apa">APA</TabsTrigger>
                  <TabsTrigger value="chicago">CHI</TabsTrigger>
                </TabsList>

                {['mla', 'apa', 'chicago'].map((style) => (
                  <TabsContent key={style} value={style} className="mt-4 space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-xs uppercase text-slate-500">Bibliography</Label>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopy(citations[style]?.bibliography, 'Bibliography')}
                          data-testid={`copy-${style}-bib`}
                        >
                          <Copy className="w-3 h-3 mr-1" />
                          Copy
                        </Button>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg font-mono text-xs leading-relaxed">
                        {citations[style]?.bibliography || 'Loading...'}
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-xs uppercase text-slate-500">In-text</Label>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopy(citations[style]?.inText, 'In-text citation')}
                          data-testid={`copy-${style}-intext`}
                        >
                          <Copy className="w-3 h-3 mr-1" />
                          Copy
                        </Button>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg font-mono text-sm">
                        {citations[style]?.inText || 'Loading...'}
                      </div>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>

          {/* Linked Projects */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Linked Projects</CardTitle>
            </CardHeader>
            <CardContent>
              {source.projectSources?.length > 0 ? (
                <div className="space-y-2">
                  {source.projectSources.map(({ project }) => (
                    <Badge key={project.id} variant="outline" className="mr-2">
                      {project.title || project.type}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">Not linked to any projects</p>
              )}
            </CardContent>
          </Card>

          {/* Tags */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Tags</CardTitle>
            </CardHeader>
            <CardContent>
              {source.sourceTags?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {source.sourceTags.map(({ tag }) => (
                    <Badge key={tag.id} variant="secondary">
                      {tag.name}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">No tags</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SourceDetailPage;
